import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Styles/themed-styled-components';

import { logger } from '@breathelife/monitoring-frontend';
import { LeadStatus } from '@breathelife/types';

import { useCarrierContext } from '../../Hooks';
import { LeadPlatformTheme } from '../../Styles/Types';
import { StyledBadge } from './Badge';

export type Props = {
  status: LeadStatus;
};

export function StatusBadge(props: Props): ReactElement | null {
  const theme = useTheme() as LeadPlatformTheme;
  const { t } = useTranslation();
  const { leadStatuses } = useCarrierContext();

  const LeadStatusTextColors: {
    [status in LeadStatus | 'default']: { textColor: string; backgroundColor: string };
  } = useMemo(
    () => ({
      new: { textColor: theme.colors.blue[50], backgroundColor: theme.colors.blue[20] },
      invited: { textColor: theme.colors.purple[50], backgroundColor: theme.colors.purple[20] },
      planFinderStarted: { textColor: theme.colors.orange[70], backgroundColor: theme.colors.orange[20] },
      planFinderCompleted: { textColor: theme.colors.grey[80], backgroundColor: theme.colors.grey[30] },
      engaged: { textColor: theme.colors.green[50], backgroundColor: theme.colors.green[20] },
      transactionFlowStarted: { textColor: theme.colors.orange[70], backgroundColor: theme.colors.orange[20] },
      transactionFlowCompleted: { textColor: theme.colors.grey[80], backgroundColor: theme.colors.grey[30] },
      qualified: { textColor: theme.colors.green[50], backgroundColor: theme.colors.green[20] },
      submissionStarted: { textColor: theme.colors.orange[70], backgroundColor: theme.colors.orange[20] },
      submissionCompleted: { textColor: theme.colors.green[50], backgroundColor: theme.colors.green[20] },
      submissionManuallyCompleted: { textColor: theme.colors.green[50], backgroundColor: theme.colors.green[20] },
      default: { textColor: theme.colors.grey[80], backgroundColor: theme.colors.grey[30] },
    }),
    [theme],
  );

  let statusLabel: string = props.status;
  const statusLabelFromConfig = leadStatuses[props.status]?.label;
  if (statusLabelFromConfig) {
    statusLabel = t(`leadStatuses.${statusLabelFromConfig}`);
  }
  const statusColors = LeadStatusTextColors[props.status] || LeadStatusTextColors.default;

  if (!LeadStatusTextColors[props.status] || !leadStatuses[props.status]) {
    logger.error(`Unknown lead status: ${props.status}`);
  }

  return (
    <StyledBadge
      backgroundColor={statusColors.backgroundColor}
      textColor={statusColors.textColor}
      label={statusLabel}
    />
  );
}
